import React, { useState, useEffect, useRef } from 'react';
import CS from '../../styles/componentStyled';
import styled from 'styled-components';
import InputModal from '../InputModal';
import { useSetRecoilState, useRecoilValue, useRecoilState } from 'recoil';
import {
  blankDefaultMsgRecoil,
  errorMsgRecoil,
  confirmMsgRecoil,
  entrySiteModifyStateRecoil,
  addressStateRecoil,
} from '../../atom/ModalAtom';
import { socketio } from '../../api/socket.api';
import { roomAPI } from '../../api/axiosIndex';
import { Icon } from '../../component/Icon';
import DaumPostcode from 'react-daum-postcode';
import { Map, MapMarker, Circle } from 'react-kakao-maps-sdk';
import { useParams } from 'react-router-dom';
import {
  locationShareStateRecoil,
  roomPeoplesUserIdsStateRecoil,
} from '../../atom/RoomAtom';
import { RoomInforDto } from '../../model/room.dto';

declare global {
  interface Window {
    kakao: any;
  }
}

interface EntrySiteModifyProps {
  onListUpdate: () => void;
}

const EntrySiteModify = ({ onListUpdate }: EntrySiteModifyProps) => {
  const blankMsg = useRecoilValue(blankDefaultMsgRecoil);
  const setError = useSetRecoilState(errorMsgRecoil);
  const setConfirm = useSetRecoilState(confirmMsgRecoil);

  const { id } = useParams<{ id: string }>();

  const setModalState = useSetRecoilState(entrySiteModifyStateRecoil);
  const [addressModalState, setAddressModalState] =
    useRecoilState(addressStateRecoil);

  const [address, setAddress] = useState<string>('');
  const [addressDetail, setAddressDetail] = useState<string>('');
  // 초기 지도 center 좌표때문에 위도 경도 값 키클롭스 회사 주소
  // 대전 유성구 복용동 236-3
  const [latitude, setLatitude] = useState<number>(36.3379878246925);
  const [longitude, setLongitude] = useState<number>(127.321910407564);
  const [radius, setRadius] = useState<number | undefined>(undefined);
  const [isModify, setIsModify] = useState<boolean>(false);

  // 현위치 모달 뷰 권한
  const [locationShareState, setLocationShareState] = useRecoilState(
    locationShareStateRecoil,
  );
  // 현위치 모달 뷰 권한(이 컴포넌트에서 on/off ui를 위해 사용)
  const [nowLocationShareState, setNowLocationShareState] = useState(false);

  // 방 유저 userId 배열
  const [roomPeoplesUserIds, setRoomPeoplesUserIds] = useRecoilState(
    roomPeoplesUserIdsStateRecoil,
  );

  const locationShareBoxRef = useRef<HTMLInputElement>(null);

  // 방 정보 조회
  const getRoomInfor = async () => {
    try {
      const response = await roomAPI.getRoomInfor(Number(id));
      setNowLocationShareState(response.data.isRoomLocationVisible);
    } catch (error) {
      console.error(error);
    }
  };

  // 입차지 정보 조회
  const getEntrySite = async () => {
    try {
      const response = await roomAPI.getEntrySite(Number(id));
      if (response.data) {
        setAddress(response.data.address.split('@')[0]);
        setAddressDetail(response.data.address.split('@')[1]);
        setLatitude(response.data.latitude);
        setLongitude(response.data.longitude);
        setRadius(response.data.radius);
        setIsModify(true);
      }
    } catch (error) {
      console.error(error);
    }
  };

  // 주소 검색 후 주소 클릭시 이벤트
  const handleAddressSubmit = (data: any) => {
    console.log(data);
    setAddress(String(data.address));
    setAddressModalState(false);
  };

  // 주소로 위도/경도 검색
  const geocodeAddress = (address: string) => {
    const kakao = window.kakao;
    if (!kakao || !kakao.maps) {
      console.error('Kakao Maps API가 로드되지 않았습니다.');
      return;
    }
    const geocoder = new kakao.maps.services.Geocoder();
    geocoder.addressSearch(address, (result: any, status: any) => {
      if (status === kakao.maps.services.Status.OK) {
        const newCenter = {
          lat: parseFloat(result[0].y),
          lng: parseFloat(result[0].x),
        };
        setLatitude(newCenter.lat);
        setLongitude(newCenter.lng);
      }
    });
  };

  // 좌표로 주소 검색
  const getAddressFromCoords = (lat: number, lng: number) => {
    const kakao = window.kakao;
    if (!kakao || !kakao.maps) {
      console.error('Kakao Maps API가 로드되지 않았습니다.');
      return;
    }
    const geocoder = new kakao.maps.services.Geocoder();
    const coord = new kakao.maps.LatLng(lat, lng);

    geocoder.coord2Address(
      coord.getLng(),
      coord.getLat(),
      (result: any, status: any) => {
        if (status === kakao.maps.services.Status.OK) {
          const address = result[0].address.address_name;
          setAddress(address);
        }
      },
    );
  };

  // 입차지 등록 및 수정 / 현위치 모달 뷰 권한 수정
  const handleConfirm = async () => {
    if (address === '') {
      setError('주소를 입력해 주세요.');
      return;
    }

    if (radius === undefined) {
      setError('반경을 입력해 주세요.');
      return;
    }

    const data = {
      address: address + '@' + addressDetail,
      latitude: latitude,
      longitude: longitude,
      radius: radius,
    };

    // 입차지 등록
    if (!isModify) {
      try {
        await roomAPI.postEntrySite(Number(id), data);
        setConfirm('입차지가 등록되었습니다.');
        setModalState(false);
      } catch (err) {
        console.error(err);
        setError('입차지 등록에 실패하였습니다.');
      }
    }
    // 입차지 수정
    else if (isModify) {
      try {
        await roomAPI.putEntrySite(Number(id), data);
        setConfirm('입차지가 수정되었습니다.');
        setModalState(false);
      } catch {
        setError('입차지 수정에 실패하였습니다.');
      }
    }

    // 현위치 모달 뷰 권한 수정
    const dto = {
      participantIds: roomPeoplesUserIds,
      isLocationViewer: nowLocationShareState,
    };

    try {
      await roomAPI.putLocationShare(Number(id), dto);
      socketio.entrySiteUpdate(Number(id));
      // setConfirm('입차지가 수정되었습니다.');
      setModalState(false);
    } catch {
      setError('입차지 수정에 실패하였습니다.');
    }
  };

  // 주소 변경시 위도/경도 업데이트
  useEffect(() => {
    if (address) {
      geocodeAddress(address);
    }
  }, [address]);

  // 입차지 정보 및 방 정보 조회 useEffect
  useEffect(() => {
    getEntrySite();
    getRoomInfor();
  }, []);

  return (
    <>
      <InputModal
        title="입차지 수정"
        onCancel={() => setModalState(false)}
        onConfirm={handleConfirm}
        isFullScreen={true}
      >
        <ul className="input-wrap">
          <li>
            <h2>방 유저 현위치 공유</h2>
            <CS.CtrlBox>
              <input
                type="checkbox"
                id="location_share_state"
                ref={locationShareBoxRef}
                checked={nowLocationShareState}
                onChange={(e) => {
                  setNowLocationShareState(e.target.checked);
                }}
              />
              <label htmlFor="location_share_state"></label>
            </CS.CtrlBox>
          </li>
          <li>
            <h2>
              주소
              <span className="img-box">
                <Icon name="StarIcon" size="100%" color="#FF5151" />
              </span>
            </h2>
            <TextInputWrap>
              <CS.TextInputType1
                placeholder="기본 주소"
                type="text"
                value={address}
                readOnly={true}
              />
              <CS.Btn6Type1
                type="button"
                onClick={() => setAddressModalState(true)}
              >
                주소 검색
              </CS.Btn6Type1>
            </TextInputWrap>
            <CS.TextInputType1
              placeholder="상세 주소"
              type="text"
              value={addressDetail}
              onChange={(e) => {
                setAddressDetail(e.target.value);
              }}
            />
          </li>
          <li>
            <h2>
              반경(m)
              <span className="img-box">
                <Icon name="StarIcon" size="100%" color="#FF5151" />
              </span>
            </h2>
            <CS.TextInputType1
              placeholder="반경 입력(m 기준)"
              type="number"
              value={radius}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, '');
                setRadius(value === '' ? undefined : Number(value));
              }}
            />
          </li>
          <li>
            <h2>
              지도
              <span className="img-box">
                <Icon name="StarIcon" size="100%" color="#FF5151" />
              </span>
            </h2>
            <MapBox>
              <Map
                center={{ lat: latitude, lng: longitude }}
                style={{ width: '100%', height: '100%' }}
                level={3}
                draggable={true}
                zoomable={true}
                onClick={(_t, mouseEvent) => {
                  const lat = mouseEvent.latLng.getLat();
                  const lng = mouseEvent.latLng.getLng();

                  setLatitude(lat);
                  setLongitude(lng);
                  getAddressFromCoords(lat, lng);
                  setAddressDetail('');
                }}
              >
                <MapMarker
                  position={{ lat: latitude, lng: longitude }}
                  image={{
                    src: '/icons/ico_entry_site.svg',
                    size: { width: 24, height: 24 },
                  }}
                ></MapMarker>
                {radius && (
                  <Circle
                    center={{ lat: latitude, lng: longitude }}
                    radius={radius}
                    strokeWeight={1}
                    strokeColor="#F0A400"
                    strokeOpacity={1}
                    strokeStyle="solid"
                    fillColor="#F0A400"
                    fillOpacity={0.25}
                  />
                )}
              </Map>
            </MapBox>
          </li>
        </ul>
      </InputModal>
      {addressModalState && (
        <AddressModalWrap>
          <DaumPostcode onComplete={handleAddressSubmit} autoClose={false} />
        </AddressModalWrap>
      )}
    </>
  );
};

export default EntrySiteModify;

const TextInputWrap = styled.div`
  display: flex;
  gap: 6px;
  > button {
    width: 76px;
    min-width: 76px;
  }
`;

const AddressModalWrap = styled.div`
  width: 100%;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.6);
  position: fixed;
  top: 0;
  left: 0;
  z-index: 10;

  > div {
    width: calc(100% - 40px) !important;
    height: calc(100vh - 80px) !important;
    position: absolute;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
  }
`;

export const MapBox = styled.div`
  width: 100%;
  height: calc(100vh - 496px);
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  #__react-kakao-maps-sdk___Map {
    z-index: 1;
  }
`;
